import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { OrgazoneMark, Trans } from '@orgazone/ui-web';
import { api, type RecoveryStatus } from '../api';
import { bandeauAvert, bandeauErr, bandeauOk, carte, champ, ecran, label, lienDiscret, eyebrow, heading } from './auth-chrome';
import { t } from '../i18n';

/**
 * RÉINITIALISATION DU MOT DE PASSE DU COMPTE DE SECOURS — deux temps sur une seule page : demander le
 * lien (sans jeton dans l'URL), puis poser le mot de passe (avec jeton).
 *
 * ⚠️ POURQUOI CET ÉCRAN EXISTE : le socle monte `/auth/password/{forgot,reset}` sans condition. À l'EDI,
 * aucune page ne les atteignait — le lien reçu par e-mail ne menait donc nulle part, et perdre le mot de
 * passe de secours voulait dire re-provisionner le compte à la main. Un parcours monté à moitié vaut un
 * parcours absent (§23.0).
 *
 * ⚠️ RÉSERVÉ AUX COMPTES LOCAUX : un accès Orgazone (SSO) se réinitialise à l'IdP, qui a sa propre
 * procédure. Le serveur ne dira jamais à laquelle des deux familles appartient une adresse.
 */
export function Reset() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const token = sp.get('token') ?? '';

  const [mail, setMail] = useState('');
  const [suivant, setSuivant] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [demande, setDemande] = useState(false);
  const [recovery, setRecovery] = useState<RecoveryStatus | null>(null);
  /** Le motif d'une lecture de l'état qui a ÉCHOUÉ — distinct de « pas encore lu ». */
  const [recoveryKo, setRecoveryKo] = useState<string | null>(null);

  // ⚠️ La page reste ATTEIGNABLE même sans canal e-mail — parce qu'un lien reçu AVANT une panne de
  // messagerie doit encore pouvoir être consommé (le second temps, lui, ne dépend d'aucun envoi). Seule
  // la DEMANDE de lien est retirée quand le serveur dit qu'elle n'aboutirait pas.
  // ⚠️ ET ELLE N'EST OFFERTE QUE CONFIRMÉE : un état illisible se DIT avec son motif, il n'ouvre pas le formulaire — la
  //    page annoncerait ensuite « un lien vient d'être envoyé » sans savoir qu'un envoi est possible. C'est la règle de
  //    la connexion, qui n'offre « mot de passe oublié » que sur un état confirmé.
  useEffect(() => {
    let vivant = true;
    api.recoveryStatus()
      .then((r) => { if (vivant) setRecovery(r); })
      .catch((e: unknown) => { if (vivant) setRecoveryKo(e instanceof Error ? e.message : String(e)); });
    return () => { vivant = false; };
  }, []);

  const demanderLien = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      await api.forgotPassword(mail.trim().toLowerCase());
      setDemande(true);
    } catch (e) {
      setErr(e instanceof Error && e.message ? e.message : t('La demande n’a pas pu être enregistrée.'));
    } finally {
      setBusy(false);
    }
  };

  const put = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr('');
    // Contrôles LOCAUX de confort uniquement — la règle qui fait foi est celle du serveur (longueur
    // minimale, liste noire), et c'est son refus qui s'affiche si elle n'est pas satisfaite.
    if (suivant !== confirmation) { setErr(t('La confirmation ne correspond pas.')); return; }
    setBusy(true);
    try {
      await api.resetPassword(token, suivant);
      nav('/login', { replace: true });
    } catch (e) {
      setErr(e instanceof Error && e.message ? e.message : t('Le mot de passe n’a pas pu être posé.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={ecran}>
      <form onSubmit={token ? put : demanderLien} style={carte}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 22 }}>
          <div style={{ marginBottom: 12 }}><OrgazoneMark size={40} /></div>
          <div style={eyebrow}>{t('Back-office')}</div>
          <div style={heading}>{token ? t('Nouveau mot de passe') : t('Mot de passe oublié')}</div>
        </div>

        {err && <div style={{ ...bandeauErr, marginBottom: 14 }}>{err}</div>}

        {token ? (
          <>
            <div style={{ marginBottom: 14 }}>
              <label style={label} htmlFor="bo-new">{t('Nouveau mot de passe')}</label>
              <input
                id="bo-new" style={champ} type="password" value={suivant} autoComplete="new-password" required autoFocus
                onChange={(e) => { setSuivant(e.target.value); setErr(''); }}
              />
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={label} htmlFor="bo-confirm">{t('Confirmer')}</label>
              <input
                id="bo-confirm" style={champ} type="password" value={confirmation} autoComplete="new-password" required
                onChange={(e) => { setConfirmation(e.target.value); setErr(''); }}
              />
            </div>
            <button type="submit" disabled={busy} className="btn-cta" style={{ width: '100%', height: 46, cursor: busy ? 'wait' : undefined }}>
              {busy ? t('Enregistrement…') : t('Définir le mot de passe')}
            </button>
          </>
        ) : demande ? (
          // ⚠️ La route est NON-ÉNUMÉRANTE (200 même si l'adresse est inconnue) : l'écran dit donc « SI un
          // compte existe », jamais « e-mail envoyé ». L'affirmer inventerait une certitude que le serveur
          // a délibérément refusé de donner.
          <div style={bandeauOk}>
            <Trans k='Demande enregistrée. <1>Si un compte local existe pour cette adresse</1>, un lien de réinitialisation vient de lui être envoyé — il expire rapidement. Rien n’est confirmé au-delà : cette page ne sait pas si l’adresse correspond à un compte.' parts={[(x) => <strong>{x}</strong>]} /></div>
        ) : recoveryKo !== null ? (
          <div style={bandeauErr}>{t('L’état de la messagerie n’a pas pu être lu : {motif}', { motif: recoveryKo })}</div>
        ) : recovery === null ? (
          <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)', textAlign: 'center' }}>{t('Lecture…')}</div>
        ) : !recovery.available ? (
          // Le canal n'existe pas : on ne montre PAS un formulaire qui ne peut pas aboutir.
          <div style={bandeauAvert}>
            {/* ⚠️ DEUX PHRASES ENTIÈRES : le motif du serveur peut MANQUER (`string | null`), et un trou
                vide laissait deux espaces au milieu de la phrase. Le traducteur reçoit ici deux
                phrases complètes plutôt qu'une phrase à ponctuation conditionnelle. */}
            {recovery.reason
              ? <Trans k='<1>Réinitialisation par e-mail indisponible.</1> {reason} Un accès Orgazone (SSO) se réinitialise depuis l’IdP ; le mot de passe du compte de secours, lui, se change une fois connecté.' vars={{ reason: recovery.reason }} parts={[(x) => <strong>{x}</strong>]} />
              : <Trans k='<1>Réinitialisation par e-mail indisponible.</1> Un accès Orgazone (SSO) se réinitialise depuis l’IdP ; le mot de passe du compte de secours, lui, se change une fois connecté.' parts={[(x) => <strong>{x}</strong>]} />}</div>
        ) : (
          <>
            <div style={bandeauAvert}>
              <Trans k='Réservé au <1>compte de secours local</1>. Un accès Orgazone (SSO) se réinitialise depuis l’IdP, pas ici.' parts={[(x) => <strong>{x}</strong>]} /></div>
            <div style={{ marginBottom: 18 }}>
              <label style={label} htmlFor="bo-reset-mail">{t('Adresse e-mail')}</label>
              <input
                id="bo-reset-mail" style={champ} type="email" value={mail} autoComplete="username" required autoFocus
                onChange={(e) => { setMail(e.target.value); setErr(''); }}
              />
            </div>
            <button type="submit" disabled={busy} className="btn-cta" style={{ width: '100%', height: 46, cursor: busy ? 'wait' : undefined }}>
              {busy ? t('Envoi…') : t('Recevoir un lien')}
            </button>
          </>
        )}

        <button type="button" onClick={() => nav('/login')} style={lienDiscret}>
          
          {t('Retour à la connexion')}
        </button>
      </form>
    </div>
  );
}
